import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["search", "results", "selected", "hiddenInputs"]
  static values = {
    searchUrl: String,
    selected: Array,
    fieldName: { type: String, default: "box_set[author_ids][]" }
  }

  connect() {
    this.searchTimeout = null
    this.renderSelected()
  }

  search() {
    const query = this.searchTarget.value.trim()

    clearTimeout(this.searchTimeout)

    if (query.length < 2) {
      this.resultsTarget.innerHTML = ""
      this.resultsTarget.classList.add("hidden")
      return
    }

    this.searchTimeout = setTimeout(() => this.performSearch(query), 300)
  }

  async performSearch(query) {
    try {
      const response = await fetch(`${this.searchUrlValue}?q=${encodeURIComponent(query)}`, {
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (!response.ok) throw new Error("Search failed")

      const authors = await response.json()
      this.renderResults(authors)
    } catch (error) {
      this.resultsTarget.innerHTML = `<div class="p-3 text-sm text-red-600">Search failed. Please try again.</div>`
      this.resultsTarget.classList.remove("hidden")
    }
  }

  renderResults(authors) {
    if (authors.length === 0) {
      this.resultsTarget.innerHTML = `<div class="p-3 text-sm text-beige-500 font-fira">No authors found</div>`
      this.resultsTarget.classList.remove("hidden")
      return
    }

    const selectedIds = this.selectedValue.map(a => a.id)

    const html = authors.map(author => {
      const isSelected = selectedIds.includes(author.id)
      return `
        <button type="button"
                class="w-full flex items-center gap-3 p-2 hover:bg-beige-50 rounded-lg text-left transition-colors ${isSelected ? 'opacity-50 cursor-not-allowed' : ''}"
                data-action="author-selector#select"
                data-author-id="${author.id}"
                data-author-name="${this.escapeAttr(author.name)}"
                data-author-photo="${this.escapeAttr(author.photo_url || '')}"
                ${isSelected ? 'disabled' : ''}>
          <div class="w-8 h-8 flex-shrink-0 bg-beige-200 rounded-full overflow-hidden">
            ${author.photo_url ? `<img src="${this.escapeAttr(author.photo_url)}" alt="" class="w-full h-full object-cover">` : `<div class="w-full h-full flex items-center justify-center text-beige-500 text-xs font-semibold">${this.escapeHtml(author.name.charAt(0))}</div>`}
          </div>
          <div class="min-w-0 flex-1">
            <p class="text-sm font-medium text-beige-900 truncate">${this.escapeHtml(author.name)}</p>
          </div>
          ${isSelected ? '<span class="text-xs text-green-600 font-fira">Added</span>' : ''}
        </button>
      `
    }).join("")

    this.resultsTarget.innerHTML = html
    this.resultsTarget.classList.remove("hidden")
  }

  select(event) {
    const button = event.currentTarget
    if (button.disabled) return

    const author = {
      id: parseInt(button.dataset.authorId),
      name: button.dataset.authorName,
      photo_url: button.dataset.authorPhoto
    }

    const currentSelected = [...this.selectedValue]
    if (!currentSelected.find(a => a.id === author.id)) {
      currentSelected.push(author)
      this.selectedValue = currentSelected
      this.renderSelected()

      button.classList.add("opacity-50", "cursor-not-allowed")
      button.disabled = true
      const addedSpan = document.createElement("span")
      addedSpan.className = "text-xs text-green-600 font-fira"
      addedSpan.textContent = "Added"
      button.appendChild(addedSpan)
    }

    this.searchTarget.value = ""
    this.resultsTarget.innerHTML = ""
    this.resultsTarget.classList.add("hidden")
  }

  remove(event) {
    const authorId = parseInt(event.currentTarget.dataset.authorId)
    this.selectedValue = this.selectedValue.filter(a => a.id !== authorId)
    this.renderSelected()
  }

  renderSelected() {
    const authors = this.selectedValue

    if (authors.length === 0) {
      this.selectedTarget.innerHTML = `
        <div class="text-center py-4 text-beige-400 font-fira text-sm">
          No authors selected. Use the search above to add authors.
        </div>
      `
      this.hiddenInputsTarget.innerHTML = ""
      return
    }

    const html = authors.map(author => `
      <div class="flex items-center gap-3 p-2 bg-beige-50 rounded-lg group">
        <div class="w-8 h-8 flex-shrink-0 bg-beige-200 rounded-full overflow-hidden">
          ${author.photo_url ? `<img src="${this.escapeAttr(author.photo_url)}" alt="" class="w-full h-full object-cover">` : `<div class="w-full h-full flex items-center justify-center text-beige-500 text-xs font-semibold">${this.escapeHtml(author.name.charAt(0))}</div>`}
        </div>
        <div class="min-w-0 flex-1">
          <p class="text-sm font-medium text-beige-900 truncate">${this.escapeHtml(author.name)}</p>
        </div>
        <button type="button"
                class="p-1 text-beige-400 hover:text-red-600 transition-colors opacity-0 group-hover:opacity-100"
                data-action="author-selector#remove"
                data-author-id="${author.id}"
                title="Remove">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    `).join("")

    this.selectedTarget.innerHTML = html

    const hiddenHtml = authors.map(author =>
      `<input type="hidden" name="${this.fieldNameValue}" value="${author.id}">`
    ).join("")

    this.hiddenInputsTarget.innerHTML = hiddenHtml
  }

  hideResults(event) {
    if (this.resultsTarget.contains(event.relatedTarget)) return

    setTimeout(() => {
      this.resultsTarget.classList.add("hidden")
    }, 200)
  }

  escapeHtml(text) {
    if (!text) return ""
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  escapeAttr(text) {
    if (!text) return ""
    return text.replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }
}
